import { log } from '../log';
import { AIContextItem, AIContextProvider, AIContextProviderType, AIContextSearchQuery } from '.';

export abstract class AbstractAIContextProvider<T extends AIContextItem>
  implements AIContextProvider<T>
{
  #selectedContextItems: T[] = [];

  type: AIContextProviderType;

  protected onContextItemAdded?: (contextItem: T) => void;

  protected onBeforeContextItemRemoved?: (contextItem: T) => void;

  protected onBeforeAllContextItemsRemoved?: () => void;

  constructor(type: AIContextProviderType = 'open_tab') {
    this.#selectedContextItems = [];
    this.type = type;
  }

  protected notifyContextItemAdded(contextItem: T): void {
    if (this.onContextItemAdded) {
      this.onContextItemAdded(contextItem);
    }
  }

  protected notifyContextItemToBeRemoved(contextItem: T): void {
    if (this.onBeforeContextItemRemoved) {
      this.onBeforeContextItemRemoved(contextItem);
    }
  }

  protected notifyAllContextItemsToBeRemoved(): void {
    if (this.onBeforeAllContextItemsRemoved) {
      this.onBeforeAllContextItemsRemoved();
    }
  }

  #findItemWithId(id: string): T | undefined {
    return this.#selectedContextItems.find((item) => item.id === id);
  }

  #findItemIndex(id: string): number {
    const index = this.#selectedContextItems.findIndex((item) => item.id === id);
    if (index === -1) {
      const error = new Error(`Item with id ${id} not found in context items.`);
      log.error(error);
      throw error;
    }
    return index;
  }

  addSelectedContextItem(contextItem: T): void {
    if (contextItem.metadata.subType !== this.type) {
      log.error(
        `Context item type "${contextItem.metadata.subType}" does not match context provider type "${this.type}"`,
      );
    } else if (this.#findItemWithId(contextItem.id)) {
      log.error(`Context item with ID "${contextItem.id}" already exists`);
    } else {
      this.#selectedContextItems.push(contextItem);
      this.notifyContextItemAdded(contextItem);
    }
  }

  removeSelectedContextItem(id: string): void {
    const index = this.#findItemIndex(id);
    this.notifyContextItemToBeRemoved(this.#selectedContextItems[index]);
    this.#selectedContextItems.splice(index, 1);
  }

  clearSelectedContextItems(): void {
    if (this.#selectedContextItems.length) {
      this.notifyAllContextItemsToBeRemoved();
      this.#selectedContextItems = [];
    }
  }

  replaceSelectedContextItem(oldItem: T, newItem: T): void {
    const index = this.#findItemIndex(oldItem.id);
    this.#selectedContextItems[index] = newItem;
  }

  getSelectedContextItems(): T[] {
    return this.#selectedContextItems;
  }

  abstract searchContextItems(query: AIContextSearchQuery): Promise<T[]>;

  abstract retrieveSelectedContextItemsWithContent(): Promise<T[]>;
}
